// The opening hours editor shared by the setup wizard and the settings page: one row per day (Monday first) with an
// open/closed switch, a status tag with a symbol, and up to three ranges of opening and closing times. The times are
// digits and stay LTR in Arabic. Validation (overlaps, past midnight, at least one open day) is done by the caller
// with the core rules, so the editor itself never refuses a keystroke.
import { MAX_RANGES_PER_DAY, UI, type HoursRange, type Lang, type Weekday } from '@ringring/core';
import { NEXT_RANGE, dayLabel, type DayHours } from './hours';

export interface HoursEditorProps {
  lang: Lang;
  days: DayHours[];
  onChange: (days: DayHours[]) => void;
  /** Prefix of the field ids, so two editors on one page never collide. */
  idPrefix?: string;
}

export function HoursEditor({ lang, days, onChange, idPrefix = 'hours' }: HoursEditorProps) {
  const t = UI[lang];
  const setDay = (day: Weekday, patch: Partial<DayHours>) => onChange(days.map((d) => (d.day === day ? { ...d, ...patch } : d)));
  const setRange = (day: Weekday, i: number, patch: Partial<HoursRange>) =>
    onChange(days.map((d) => (d.day === day ? { ...d, ranges: d.ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) } : d)));
  const addRange = (day: Weekday) => onChange(days.map((d) => (d.day === day && d.ranges.length < MAX_RANGES_PER_DAY ? { ...d, ranges: [...d.ranges, { ...NEXT_RANGE }] } : d)));
  const removeRange = (day: Weekday, i: number) => onChange(days.map((d) => (d.day === day && d.ranges.length > 1 ? { ...d, ranges: d.ranges.filter((_, j) => j !== i) } : d)));

  return (
    <div className="hours-editor">
      {days.map((d) => {
        const dayId = `${idPrefix}-${d.day}`;
        return (
          <div className="hours-row" key={d.day}>
            <div className="row">
              <label className="check day" htmlFor={dayId}>
                <input id={dayId} type="checkbox" checked={d.open} onChange={(e) => setDay(d.day, { open: e.target.checked })} />
                <span>{dayLabel(lang, d.day)}</span>
              </label>
              <span className={`tag ${d.open ? 'ready' : ''}`}>{d.open ? `✓ ${t.open_day}` : `— ${t.closed_day}`}</span>
            </div>
            {d.ranges.map((r, i) => (
              <div className="row hours-range" key={i}>
                <label className="check">
                  <span>{t.opens_at}</span>
                  <input type="time" dir="ltr" value={r.open} disabled={!d.open} onChange={(e) => setRange(d.day, i, { open: e.target.value })} />
                </label>
                <label className="check">
                  <span>{t.closes_at}</span>
                  <input type="time" dir="ltr" value={r.close} disabled={!d.open} onChange={(e) => setRange(d.day, i, { close: e.target.value })} />
                </label>
                {d.ranges.length > 1 ? (
                  <button className="btn secondary icon" type="button" aria-label={t.remove_range} disabled={!d.open} onClick={() => removeRange(d.day, i)}>
                    ✕
                  </button>
                ) : null}
              </div>
            ))}
            {d.open && d.ranges.length < MAX_RANGES_PER_DAY ? (
              <div className="row hours-range">
                <button className="btn secondary" type="button" onClick={() => addRange(d.day)}>
                  + {t.add_range}
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
